import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../data/translations';
import { Menu, X, Calendar } from 'lucide-react';
import { Button } from './ui/button';

export const Header = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
      
      // Detect active section
      const sections = ['home', 'services', 'about', 'appointments', 'contact'];
      for (const section of sections.reverse()) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100) {
            setActiveSection(section);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80; // Header height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { id: 'home', label: t(translations.nav.home) },
    { id: 'services', label: t(translations.nav.services) },
    { id: 'about', label: t(translations.nav.about) },
    { id: 'appointments', label: t(translations.nav.appointments) },
    { id: 'contact', label: t(translations.nav.contact) },
  ];

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'backdrop-blur-xl bg-white/80 border-b border-slate-200/50 shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-6 md:px-12 lg:px-24 max-w-7xl">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <button 
            onClick={() => scrollToSection('home')} 
            className="flex flex-col text-left"
            data-testid="logo-link"
          >
            <span className="font-heading text-lg md:text-xl font-bold text-primary-deep tracking-tight">
              Miguel Andrade
            </span>
            <span className="text-xs text-slate-500 font-body -mt-1">
              Fisioterapia & Reabilitação
            </span>
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                data-testid={`nav-${item.id}`}
                className={`text-sm font-medium transition-colors duration-200 ${
                  activeSection === item.id
                    ? 'text-primary-deep'
                    : 'text-slate-600 hover:text-primary-deep'
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-4">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              data-testid="language-toggle"
              className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors duration-200 text-sm font-medium text-slate-700"
            >
              <span className={language === 'pt' ? 'opacity-100' : 'opacity-50'}>PT</span>
              <span className="text-slate-300">|</span>
              <span className={language === 'en' ? 'opacity-100' : 'opacity-50'}>EN</span>
            </button>

            {/* CTA Button */}
            <Button
              onClick={() => scrollToSection('appointments')}
              data-testid="header-cta"
              className="hidden sm:flex h-10 px-6 rounded-full bg-primary-deep text-white hover:bg-accent-blue transition-colors duration-200 shadow-md hover:shadow-lg"
            >
              <Calendar className="w-4 h-4 mr-2" />
              {t(translations.hero.cta)}
            </Button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 text-slate-700 hover:text-primary-deep transition-colors"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        data-testid="mobile-menu"
        className={`lg:hidden fixed inset-x-0 top-20 bg-white border-b border-slate-200 shadow-lg transition-all duration-300 ${
          isMobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'
        }`}
      >
        <nav className="container mx-auto px-6 py-6 flex flex-col gap-4">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              data-testid={`mobile-nav-${item.id}`}
              className={`text-base font-medium py-2 text-left transition-colors duration-200 ${
                activeSection === item.id
                  ? 'text-primary-deep'
                  : 'text-slate-600 hover:text-primary-deep'
              }`}
            >
              {item.label}
            </button>
          ))}
          
          <div className="flex items-center justify-between pt-4 border-t border-slate-100">
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors duration-200 text-sm font-medium text-slate-700"
            >
              <span className={language === 'pt' ? 'opacity-100' : 'opacity-50'}>PT</span>
              <span className="text-slate-300">|</span>
              <span className={language === 'en' ? 'opacity-100' : 'opacity-50'}>EN</span>
            </button>

            <Button 
              onClick={() => scrollToSection('appointments')}
              className="h-10 px-6 rounded-full bg-primary-deep text-white hover:bg-accent-blue transition-colors duration-200"
            >
              <Calendar className="w-4 h-4 mr-2" />
              {t(translations.hero.cta)}
            </Button>
          </div>
        </nav>
      </div>
    </header>
  );
};
